/**
 * Location Context Provider
 *
 * Manages the user's selected province and city across the app.
 * Persists selection to localStorage.
 */

/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  Province,
  City,
  PROVINCES,
  getProvince,
  getCity,
  isLocationSupported,
  DEFAULT_PROVINCE,
  DEFAULT_CITY,
} from "../config/locations";

interface LocationContextType {
  // Current selection
  province: Province;
  city: City | null;
  provinceId: string;
  cityId: string | null;

  // Setters
  setProvince: (provinceId: string) => void;
  setCity: (cityId: string | null) => void;
  setLocation: (provinceId: string, cityId: string | null) => void;

  // Helpers
  isSupported: boolean;
  isProvinceSupported: boolean;
  isCitySupported: boolean;
  locationLabel: string;
  shortLabel: string;

  // All data
  allProvinces: Province[];
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "builderbuddy_location";

interface StoredLocation {
  provinceId: string;
  cityId: string | null;
}

export function LocationProvider({ children }: { children: ReactNode }) {
  const [provinceId, setProvinceId] = useState<string>(DEFAULT_PROVINCE);
  const [cityId, setCityId] = useState<string | null>(DEFAULT_CITY);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const {
          provinceId: storedProvince,
          cityId: storedCity,
        }: StoredLocation = JSON.parse(stored);
        if (getProvince(storedProvince)) {
          setProvinceId(storedProvince);
          setCityId(storedCity);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    const location: StoredLocation = { provinceId, cityId };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  }, [provinceId, cityId]);

  const province = getProvince(provinceId) || getProvince(DEFAULT_PROVINCE)!;
  const city = cityId ? getCity(provinceId, cityId) || null : null;

  const setProvince = (newProvinceId: string) => {
    const newProvince = getProvince(newProvinceId);
    if (newProvince) {
      setProvinceId(newProvinceId);
      // Reset city to first supported city in new province, or null
      const firstSupported = newProvince.cities.find((c) => c.isSupported);
      setCityId(firstSupported?.id || newProvince.cities[0]?.id || null);
    }
  };

  const setCity = (newCityId: string | null) => {
    if (newCityId === null || getCity(provinceId, newCityId)) {
      setCityId(newCityId);
    }
  };

  const setLocation = (newProvinceId: string, newCityId: string | null) => {
    const newProvince = getProvince(newProvinceId);
    if (newProvince) {
      setProvinceId(newProvinceId);
      if (newCityId === null || getCity(newProvinceId, newCityId)) {
        setCityId(newCityId);
      } else {
        setCityId(null);
      }
    }
  };

  const isProvinceSupported = province.isSupported;
  const isCitySupported = city?.isSupported || false;
  const isSupported = isLocationSupported(provinceId, cityId || undefined);

  const locationLabel = city
    ? `${city.name}, ${province.abbreviation}`
    : province.name;

  const shortLabel = city?.name || province.abbreviation;

  const value: LocationContextType = {
    province,
    city,
    provinceId,
    cityId,
    setProvince,
    setCity,
    setLocation,
    isSupported,
    isProvinceSupported,
    isCitySupported,
    locationLabel,
    shortLabel,
    allProvinces: PROVINCES,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
}

// Note: Import PROVINCES, getProvince, getCity, isLocationSupported directly from '../config/locations' if needed
